import React, { useEffect, useState } from "react";
import Slider from "react-slick";
import parse from "html-react-parser";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useThemeMode } from "../../../context/contextTheme";
import hexToRGBA from "../../../utils/hexToRGBA";
import { getFeed } from "../../../api/get/get-feed";

const NewsCarousel = () => {
  const { theme } = useThemeMode();
  const [feedData, setFeedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNews = async () => {
      setLoading(true);
      try {
        const data = await getFeed();
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(data, "text/xml");
        const items = xmlDoc.querySelectorAll("item");

        const feedItems = Array.from(items).map((item) => ({
          title: item.querySelector("title")?.textContent,
          description: item.querySelector("description")?.textContent,
          link: item.querySelector("link")?.textContent,
        }));

        setFeedData(feedItems.slice(0, 5));
        setError(null);
      } catch (err) {
        console.error("RSS Fetch Error:", err);
        setError("Failed to load security feed.");
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  const settings = {
    dots: true,
    infinite: true,
    speed: 800,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 6000,
    arrows: false,
    fade: true,
  };

  return (
    <div className="modern-news-container">
      <div
        className="news-card border-0 rounded-4 shadow-lg overflow-hidden"
        style={{
          maxWidth: 360,
          background: theme.background,
          border: `1px solid ${hexToRGBA(theme.border, 0.5)}`,
        }}
      >
        <div className="d-flex align-items-center justify-content-between p-4 pb-2">
          <div className="d-flex align-items-center gap-2">
            <span className="live-dot pulse"></span>
            <h6 className="m-0 fw-bold text-uppercase tracking-wider" style={{ color: theme.textColor, fontSize: '0.8rem', opacity: 0.8 }}>
              Security Feed
            </h6>
          </div>
          <i className="bi bi-rss-fill" style={{ color: theme.primary }}></i>
        </div>

        {loading ? (
          <div className="d-flex flex-column align-items-center justify-content-center py-5">
            <div className="spinner-border spinner-border-sm mb-2" style={{ color: theme.primary }}></div>
            <span className="small opacity-50">Fetching Intel...</span>
          </div>
        ) : error ? (
          <div className="text-center py-5 px-3">
            <i className="bi bi-exclamation-triangle text-danger fs-3 mb-2"></i>
            <p className="small text-danger m-0">{error}</p>
          </div>
        ) : (
          <div className="px-3 pb-4">
            <Slider {...settings}>
              {feedData.map((item, index) => (
                <div key={index} className="outline-none">
                  <div
                    className="p-3 rounded-4"
                    style={{
                      background: hexToRGBA(theme.primary, 0.04),
                      border: `1px solid ${hexToRGBA(theme.primary, 0.1)}`,
                      minHeight: '220px'
                    }}
                  >
                    <h5
                      className="fw-bold mb-2"
                      style={{
                        color: theme.textColor,
                        fontSize: '1.05rem',
                        display: '-webkit-box',
                        WebkitLineClamp: '2',
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {item.title}
                    </h5>

                    {item.description && (
                      <div
                        className="small mb-3"
                        style={{
                          color: theme.textMuted,
                          display: '-webkit-box',
                          WebkitLineClamp: '3',
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: '1.5'
                        }}
                      >
                        {parse(item.description)}
                      </div>
                    )}

                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-link-modern"
                      style={{ color: theme.primary, textDecoration: 'none', fontWeight: 'bold', fontSize: '0.85rem' }}
                    >
                      Explore Report <i className="bi bi-arrow-right ms-1"></i>
                    </a>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        )}
      </div>

      <style>{`
        .live-dot {
          width: 8px;
          height: 8px;
          background-color: #ff4d4d;
          border-radius: 50%;
          display: inline-block;
        }
        .pulse { animation: pulse-red 2s infinite; }
        @keyframes pulse-red {
          0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(255, 77, 77, 0.7); }
          70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(255, 77, 77, 0); }
          100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(255, 77, 77, 0); }
        }
        :global(.slick-dots) { bottom: -12px !important; }
        :global(.slick-dots li button:before) { color: ${theme.primary} !important; font-size: 6px !important; }
      `}</style>
    </div>
  );
};

export default NewsCarousel;
